// client/src/features/payments/search.ts — SCR-RENT-003's URL state · PAY-003 (04 §9.2.3, §8.5).
//
// §9.2.3 is normative for every LIST screen: filters, pagination and sort are search params
// validated by a Zod schema, so a filtered list is a link, refresh keeps it and Back undoes a
// filter. `features/rent/search.ts` is the shape this copies.
//
// The filters are `ListPayments`' own — contract, property, date interval, method and
// `withCredit` — under the same names the endpoint uses, which is what lets `toPaymentQuery`
// be a rename-free pass-through and keeps the URL readable next to the API it drives.
//
// `withCredit` is the one filter that is not a column. D-673 makes tenant credit the difference
// between a payment and its splits, computed and never stored, so „arată-mi banii nerepartizați"
// cannot be a client-side filter over a page of rows — the server answers it, and this flag is
// how it is asked.

import { z } from 'zod';

import { PAYMENT_METHODS } from './enums';

/** `YYYY-MM-DD`, the shape `DateOnly` takes on the wire (D8) and `<input type="date">` writes. */
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** 02 §7.4 — `pageSize` default 25, max 200; the server refuses more rather than clamping. */
export const paymentSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  contractId: z.string().uuid().optional(),
  propertyId: z.string().uuid().optional(),
  from: dateOnly.optional(),
  to: dateOnly.optional(),
  method: z.enum(PAYMENT_METHODS).optional(),
  /**
   * Only the payments still carrying unallocated credit. Absent rather than `false` when off:
   * `withCredit=false` and no filter mean the same thing to the endpoint, and a URL that says
   * so twice is one the user cannot read.
   */
  withCredit: z.coerce.boolean().optional(),
});

export type PaymentSearch = z.infer<typeof paymentSearchSchema>;

/** Which filters are engaged — the empty state needs to know why it is empty. */
export function hasActiveFilters(search: PaymentSearch): boolean {
  return (
    search.contractId !== undefined ||
    search.propertyId !== undefined ||
    search.from !== undefined ||
    search.to !== undefined ||
    search.method !== undefined ||
    search.withCredit === true
  );
}

/** Clearing filters returns to page 1 — page 3 of a shorter set is a blank screen. */
export function clearedSearch(search: PaymentSearch): PaymentSearch {
  return { page: 1, pageSize: search.pageSize };
}
